/**
 * Module-level queued-state for the library app.
 *
 * Every grid tile and the game detail page need to know "is this game
 * in my queue?" synchronously at render time. Rather than threading
 * per-card data through the catalog response, the app fetches the
 * queue once at boot into a Set<game_id> and keeps it fresh via the
 * `library:lists-changed` CustomEvent that every list/queue mutation
 * in api.js already emits.
 *
 * Signed-out users simply have an empty set — toggles then throw the
 * 401 up to the button, which shows the sign-in nudge (same pattern
 * as the favorite toggle).
 */

import { getQueue, addQueueItem, removeQueueItem } from './api.js';

const state = {
  queueId: null,
  gameIds: new Set(),
  loaded: false,
};

let refreshing = null; // in-flight hydration chain
let dirty = false;     // loadQueue() arrived while a hydration was in flight
// Bumped by every optimistic mutation (and rollback). A GET snapshot that
// started before the latest bump predates the mutation and must not
// overwrite the set (PR #201 re-review: POST-then-stale-GET flipped a
// freshly queued game back to "+ Queue").
let mutationEpoch = 0;

/**
 * Fetch the queue and populate the set. 401-tolerant: signed-out users
 * resolve to an empty set, no throw. Fire-and-forget from boot().
 * Lazily registers the lists-changed listener on first call.
 *
 * Calls that arrive while a hydration is in flight mark the chain dirty —
 * the in-flight GET predates whatever prompted them, so one trailing
 * refresh runs and every caller resolves with the final state. The
 * `library:queue-loaded` signal fires once per chain, after that final
 * state, so renderers never see an intermediate stale snapshot.
 */
export function loadQueue() {
  ensureListener();
  if (refreshing) {
    dirty = true;
    return refreshing;
  }
  refreshing = (async () => {
    let applied;
    do {
      dirty = false;
      applied = await refresh();
    } while (dirty || !applied);
    document.dispatchEvent(new CustomEvent('library:queue-loaded'));
  })().finally(() => { refreshing = null; });
  return refreshing;
}

/** @returns false when the snapshot was superseded by a mutation mid-GET. */
async function refresh() {
  const epochAtStart = mutationEpoch;
  try {
    const queue = await getQueue();
    if (epochAtStart !== mutationEpoch) return false; // stale — caller refetches
    state.queueId = queue.id;
    state.gameIds = new Set((queue.items || []).map((i) => Number(i.game_id)));
    state.loaded = true;
  } catch (err) {
    if (err?.status !== 401) {
      console.warn('[queue-state] load failed:', err);
    }
  }
  return true; // errors keep the fail-quiet contract and never loop
}

let listening = false;
function ensureListener() {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  // Any list mutation (including queue detail-page removes, which go
  // through the generic list-item endpoints) re-syncs the set.
  document.addEventListener('library:lists-changed', () => { loadQueue(); });
}

/** Synchronous render-time check. */
export function isQueued(gameId) {
  return state.gameIds.has(Number(gameId));
}

/** The queue's list id, once known (null before first load/add). */
export function getQueueId() {
  return state.queueId;
}

/**
 * Set a game's queued state. Optimistic: the set flips immediately and
 * reverts on failure; the error is rethrown so the calling button can
 * show its 401 nudge.
 */
export async function setQueued(gameId, next) {
  const id = Number(gameId);
  mutationEpoch += 1;
  if (next) state.gameIds.add(id); else state.gameIds.delete(id);
  try {
    if (next) {
      const res = await addQueueItem(id);
      if (res?.queue_id) state.queueId = res.queue_id;
    } else {
      await removeQueueItem(id);
    }
    return next;
  } catch (err) {
    mutationEpoch += 1; // the rollback is a mutation too
    if (next) state.gameIds.delete(id); else state.gameIds.add(id);
    throw err;
  }
}
